import { For, type JSX, Show } from "solid-js";
import { cn } from "@bso/shared";

export interface BarChartDatum {
  /** Unique key for the row. */
  key: string;
  /** Visible label. */
  label: string;
  /** Numeric value (>= 0). */
  value: number;
}

interface BarChartProps {
  data: BarChartDatum[];
  /** Accessible name for the whole chart. */
  ariaLabel: string;
  /** Tailwind class for the filled bar (theme token). Defaults to bg-primary. */
  barClass?: string;
  /** Suffix appended to the value in the row (e.g. unit). */
  class?: string;
}

/**
 * Dependency-free, theme-aware horizontal bar chart built with CSS widths.
 *
 * Each row is a labeled track + a filled bar whose width is a percentage of
 * the max value, so it scales with any data range. Rendered as an accessible
 * list (role=img + per-row aria-labels); honors `prefers-reduced-motion` via
 * the global transition guard. No SVG canvas, no horizontal scroll.
 */
export function BarChart(props: BarChartProps): JSX.Element {
  const max = () => Math.max(1, ...props.data.map((d) => d.value));

  const pct = (value: number) => {
    if (value <= 0) {
      return 0;
    }
    // Floor non-zero bars at a visible sliver so single hits are not invisible.
    return Math.max(4, Math.round((value / max()) * 100));
  };

  return (
    <div role="img" aria-label={props.ariaLabel} class={cn("flex flex-col gap-2.5", props.class)}>
      <For each={props.data}>
        {(d) => (
          <div class="flex items-center gap-3" aria-label={`${d.label}: ${d.value}`}>
            <span class="w-28 shrink-0 truncate text-right text-xs font-medium text-muted-foreground">
              {d.label}
            </span>
            <div class="relative h-5 flex-1 overflow-hidden rounded-md bg-muted/40">
              <Show when={d.value > 0}>
                <div
                  class={cn(
                    "absolute inset-y-0 left-0 rounded-md transition-[width] duration-500 ease-out",
                    props.barClass ?? "bg-primary",
                  )}
                  style={{ width: `${pct(d.value)}%` }}
                />
              </Show>
            </div>
            <span class="w-9 shrink-0 text-right text-xs font-bold tabular-nums text-foreground/85">
              {d.value}
            </span>
          </div>
        )}
      </For>
    </div>
  );
}
